// Side-effect import: registers the `beforeinstallprompt` listener at module
// load. Chrome can fire that event during initial HTML parse, before any
// useEffect runs, so the InstallPrompt's hook alone would miss it.
import '@ordo/ui/pwa-install-capture'
import './globals.css'

import { initFaro, pushError } from '@ordo/faro'
import { FaroErrorBoundary } from '@ordo/faro/error-boundary'
import { messagesFor, resolveClientLocale } from '@ordo/i18n'
import { mountScreenshotBridge } from '@ordo/screenshot-bridge'
import { DestructiveConfirmProvider } from '@ordo/ui/destructive-confirm'
import { PostHogProvider } from '@posthog/react'
import { QueryClientProvider } from '@tanstack/react-query'
import posthog from 'posthog-js'
import { createRoot } from 'react-dom/client'
import { IntlProvider } from 'react-intl'
import { RouterProvider } from 'react-router'

import { isReviewMode } from '@/components/review-mode'
import { scrubEventUrls, scrubReplayUrl } from '@/lib/analytics-url-scrub'
import { installChunkRecovery } from '@/lib/chunk-recovery'
import { bootstrapDemo, isDemoMode } from '@/lib/demo-bootstrap'
import { describeThrown, isStaleChunkMessage, STALE_CHUNK_MESSAGE } from '@/lib/error-report'
import { queryClient } from '@/lib/query-client'
import { router } from '@/routes'
import { installServiceWorkerLifecycle } from '@/sw/register'

/**
 * Product analytics + session replay (PostHog). Config arrives from the
 * server at runtime (GET /api/posthog/config) and all traffic rides the
 * same-origin /api/posthog relay — no VITE_ build-time env (one image
 * serves every environment) and no third-party hostname for ad blockers
 * to eat. Fire-and-forget from boot(): never delays first paint, and
 * silently no-ops when the server has no POSTHOG_PROJECT_TOKEN.
 */
async function initAnalytics(): Promise<void> {
  try {
    const res = await fetch('/api/posthog/config')
    if (!res.ok) {
      return
    }
    const config = (await res.json()) as { token: string | null; apiHost?: string; uiHost?: string }
    if (!config.token) {
      return
    }
    posthog.init(config.token, {
      api_host: config.apiHost ?? '/api/posthog',
      ui_host: config.uiHost,
      defaults: '2026-01-30',
      // URLs reach PostHog without their query or fragment: several pages
      // carry a credential there (see lib/analytics-url-scrub.ts).
      before_send: scrubEventUrls,
      // Replay ships DOM to a third party: keep every input masked
      // (passwords/PII never leave the page). Rendered text stays visible —
      // that's what makes a replay debuggable, and it's the same data Ordo
      // staff already see in the customers console.
      session_recording: { maskAllInputs: true, maskCapturedNetworkRequestFn: scrubReplayUrl },
    })
  } catch {
    // Analytics must never break boot.
  }
}

async function boot() {
  const rootElement = document.getElementById('root')
  if (!rootElement) {
    return
  }

  // Resolve the review-mode gate once. It folds to a constant `false` in
  // prod (the gate is `import.meta.env.DEV`-guarded), so every
  // `reviewMode`-conditional dynamic import below dead-code-eliminates —
  // along with the `@/review/*` chain those modules pull in, which aliases
  // to the dev-only `apps/ordo/tools/` tree that's absent in the prod image.
  const reviewMode = isReviewMode()

  // Install the comment system's runtime error buffer FIRST — before
  // anything else patches `window.fetch` (e.g. the demo's mock-fetch).
  // Order matters: our wrapper sees the resolved (mocked) Response and
  // records non-2xx outcomes, which is exactly what a reviewer wants to
  // attach to a pin when the comment is about a bug. Idempotent.
  //
  // Dynamic import + `reviewMode` gate: the buffer lives under `@/review/*`
  // (the dev-only `tools/` tree), so it must not be statically imported by
  // this prod-reachable entry module. It's only useful in review mode (the
  // composer snapshots it onto pins), so gating it here is also correct.
  if (reviewMode) {
    const { installErrorBuffer } = await import('@/review/runtime-error-buffer')
    installErrorBuffer()
  }

  // Client RUM (web vitals, errors, route changes) → POST /api/telemetry →
  // FARO_COLLECTOR_URL (empty = no-op proxy, so this is always safe to init).
  // AFTER the review-mode error buffer — that buffer must be the FIRST
  // window.fetch patch (comment above), and Faro's web instrumentation also
  // wraps fetch.
  initFaro({
    appName: 'ordo',
    collectorUrl: '/api/telemetry',
    environment: import.meta.env.MODE,
    ignoreErrors: [
      // Browser quirk fired when a ResizeObserver callback can't drain its
      // queue in one frame. Triggered by extension overlays and dense layout
      // work; not an app bug.
      /^ResizeObserver loop completed/,
      // Background-tab / lost-network fetch aborts. Safari uses "Load failed",
      // Chrome "Failed to fetch", Firefox "NetworkError when attempting to fetch".
      /^Load failed$/,
      /^Fetch failed$/,
      /^Failed to fetch$/,
      /^NetworkError when attempting to fetch/,
      /Load was interrupted/,
      // Service-worker update probe firing during a deploy window. Workbox
      // retries on the next page load; nothing for the user to do.
      /Failed to update a ServiceWorker for scope/,
      // Bot/scanner traffic (fixed-viewport Chrome fleets, Bytespider) failing
      // the SW script fetch. Real breakage surfaces differently ("unsupported
      // MIME type", "behind a redirect") and is deliberately NOT filtered.
      /Failed to register a ServiceWorker for scope .*unknown error occurred when fetching the script/,
      // The HTML spec's muted cross-origin error report: no message, no
      // frames (the stack is literally `at ? (<page-url>:0:0)`). 100% WebKit
      // in our data. Our bundle is same-origin and cannot produce it.
      /^Script error\.?$/,
      // Headless link scanner (Outlook Safe Links signature): zero frames,
      // fixed 1920x1080 Chrome/Windows, 41 of 42 hits on /signin, never
      // authenticated. The `\d+` matters — each Id mints its own hash.
      /Object Not Found Matching Id:\d+, MethodName:update/,
      // Faro's own wrapper for a thrown value with no enumerable keys, so
      // there is nothing to triage. `\s*$` is load-bearing: a future throw
      // that actually names its keys still alerts.
      /^Non-Error exception captured with keys:\s*$/,
      // PostHog session replay reaching into the Stripe cross-origin iframe.
      // Another origin's frame; nothing on our side to fix.
      /^SecurityError: Failed to read a named property .* from 'Window': Blocked a frame/,
      // Edge-on-iOS's built-in content extraction, running from an injected
      // inline script. No such symbol exists in our bundle.
      /^console\.error: Error during extraction:/,
      // web-vitals calling Array.prototype.at on Chrome 79 (.at landed in
      // Chrome 92). A dependency's browser-support floor, not our code.
      /\.at is not a function$/,
      // Browser auto-translate reparenting text nodes under React-owned
      // parents. 86.7% of this family is non-English locale against a 4.7%
      // baseline (es-US runs 493x the en-US rate), and 336 of 338 stacks carry
      // no ordo frame — no line of our code can branch on browser language.
      // Our own download links can no longer produce these: every
      // `body.removeChild(a)` was changed to `a.remove()`, which is a no-op on
      // a detached node. Blink wordings ONLY — WebKit's generic
      // "The object can not be found here." is deliberately left alerting,
      // because that is also what Dexie/IndexedDB throws on the iPad kiosks
      // and suppressing it would blind the offline queue.
      /^Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node\.$/,
      /^Failed to execute 'insertBefore' on 'Node': The node before which the new node is to be inserted is not a child of this node\.$/,
      // MetaMask's injected inpage.js. Ordo is a school-meals app with no
      // wallet integration (no ethereum/web3 reference anywhere in the tree).
      /^Failed to connect to MetaMask$/,
    ],
  })

  // AFTER Faro for the same fetch-patch ordering reason: replay's network
  // capture wraps window.fetch when it spins up, and (being async behind a
  // config fetch) it must layer on top of the review buffer + Faro, never
  // under them. Deliberately not awaited.
  void initAnalytics()

  // If `?demo=1`, install the fetch override BEFORE React renders.
  // The bootstrap queries (`/api/me/profile`, etc.) start firing as soon
  // as the router mounts, so the mock fetch needs to be live first.
  // The Lite Demo IS the real client — we just intercept every `/api/...`
  // call and return fixture JSON sourced from `@dataset/seed`.
  if (isDemoMode()) {
    await bootstrapDemo()
    // The bottom gradient demo stripe is intentionally NOT installed —
    // the Review chip's "Demo data" footer hint is enough signal, and
    // the stripe was redundant chrome that competed with the app UI.
    // `installDemoStripe()` stays exported for ad-hoc use if needed.
  }

  // Recover a missing old route chunk once per loaded build. A permanent
  // per-tab marker prevented long-lived Serve tabs recovering after later
  // deploys. A cross-build cooldown still prevents rollout replica loops.
  installChunkRecovery(window)

  // `onCaughtError` replaces react-dom's default, which is literally
  // `console.error(error)` — the THIRD emitter for a single render error
  // (after react-router's render log and its default ErrorBoundary log).
  // Faro captures console.error as an exception, so each emitter minted its
  // own alert hash. Reporting happens once, in `onError` below.
  const root = createRoot(rootElement, {
    onCaughtError: () => {},
  })
  // Static-label locale for the whole tree. Ordo is the customer-facing app,
  // so a family whose device is Spanish gets Spanish chrome immediately;
  // `resolveClientLocale` honors an explicit localStorage choice first and
  // normalizes the poisoned `es_US` values the retired module wrote.
  //
  // `User.locale` is deliberately NOT passed, even though the resolver accepts
  // it. That column is a stamped default, not a preference: 31,286 of 31,290
  // live users read `en_US` and exactly 4 read `es_US` (measured 2026-09-15),
  // so feeding it in would shadow `navigator.language` and pin every
  // Spanish-browser family to English — which is what the retired
  // `useSyncProfileLocale` did. Pass it once a real locale picker writes it.
  //
  // Menu item names are content localization and come from approved
  // `MenuStringTranslation` rows, never from this catalog.
  const locale = resolveClientLocale()

  const app = (
    <PostHogProvider client={posthog}>
      <FaroErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <IntlProvider locale={locale} messages={messagesFor(locale)} defaultLocale="en-US">
            <DestructiveConfirmProvider>
              <RouterProvider
                router={router}
                // THE single reporting site for route errors. react-router calls
                // this INSTEAD of its own
                // `console.error('React Router caught the following error during render')`
                // (chunk-ZZNWZ5Q3.js:7175), and calls it once per error rather
                // than on every re-render. The root `errorElement` and
                // `onCaughtError` above deliberately do not re-report, or one
                // failure would be three events again.
                onError={(error) => {
                  const message = describeThrown(error)
                  if (isStaleChunkMessage(message)) {
                    // Collapse ~227 chunk-hash-bearing values into ONE alert.
                    // The URL moves to context, which the rule does not group by.
                    pushError(new Error(STALE_CHUNK_MESSAGE), { detail: message })
                    return
                  }
                  pushError(error)
                }}
              />
            </DestructiveConfirmProvider>
          </IntlProvider>
        </QueryClientProvider>
      </FaroErrorBoundary>
    </PostHogProvider>
  )

  // When the page boots with `?review=1` (or the sticky localStorage
  // flag set previously), wrap the app with the pin-and-comment review
  // overlay. Dev-only — `isReviewMode` returns false in production
  // builds, so the dynamic `import('@/components/review-mount')` below
  // (and its `@/review/*` chain) tree-shakes away.
  if (reviewMode) {
    const { ReviewMount } = await import('@/components/review-mount')
    root.render(<ReviewMount>{app}</ReviewMount>)
  } else {
    root.render(app)
  }

  // Plan 23 — wire the service-worker lifecycle (force-update reload
  // overlay + periodic update check + the requestForceUpdate API used
  // by the DevicesPage). The vite-plugin-pwa runtime registers the SW
  // itself via `injectRegister: 'auto'`; this layer just adds the
  // page-side coordination on top.
  installServiceWorkerLifecycle()
}

// Workspaces annotator handshake — no-op unless this page is framed inside
// previews.ordo.com (lazy-loads html2canvas only then).
mountScreenshotBridge()

void boot()
